import { Injectable } from '@angular/core';
import { Task } from 'src/core/master-data/domain/task';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { GarbageCollectorService } from 'src/core/garbage-collector/domain/garbage-collector.service';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { ConfirmCloseTaskRequest } from './confirm-close-task-request';
import { ConfirmCloseTaskQueueElement } from 'src/core/queue/domain/elements/confirm-close-task-queue-element';
import { QueueElement, QueueElementError } from 'src/core/queue/domain/queue-element';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { UnauthorizedException } from 'src/core/authentication/domain/exceptions/unauthorized-exception';

@Injectable({
  providedIn: 'any',
})
export class ConfirmCloseTask {
  private isSavedToServer = false;
  private isConfirmClosedToServer = false;
  private isRetry = false;
  private task: Task;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly garbageCollector: GarbageCollectorService,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService,
    private readonly masterDataService: MasterDataService
  ) {}

  async execute(request: ConfirmCloseTaskRequest, bulkMode = false): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isSavedToServer = queueElement !== null ? queueElement.operationStatus.isSavedToServer : false;
    this.isConfirmClosedToServer = queueElement !== null ? queueElement.operationStatus.isConfirmClosedToServer : false;

    try {
      if (!this.isRetry) {
        this.task.confirmClose();
        this.markTaskAsFinalDependingOnResolution();
        await this.saveTaskIntoLocalDevice();
      }

      const taskIsInQueue = this.queueService.hasQueueElementWithTaskId(this.task.id);

      if (bulkMode) {
        await this.enqueueOperation();
        return;
      }

      if (taskIsInQueue && !this.isRetry) {
        await this.enqueueOperation();
        return;
      }

      await this.saveTaskToExternalServer(this.task);
      await this.confirmCloseTaskToExternalServer(this.task);
      await this.garbageCollector.addDirectoryToDelete(this.taskService.getAttachmentsDirectoryForTaskId(this.task.id));
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        if (this.isRetry) {
          await this.keepQueueElementForNextRetry(queueElement);
          throw exception;
        }

        await this.enqueueOperation();
        return;
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        await this.storeErrorsInQueueElement(exception, queueElement);
        throw exception; // propagate exception without doing anything else
      }

      // otherwise, rollback
      this.task.rollback();
      await this.saveTaskIntoLocalDevice();
      throw exception; // propagate exception
    }
  }

  /**
   * A retried element is still in the queue, and returning normally makes the caller drop it as processed,
   * so enqueueing a new one would move the operation behind later ones. Throwing keeps the original in
   * place, with the progress made so far, so the next retry does not repeat what already reached the server.
   */
  private async keepQueueElementForNextRetry(queueElement: ConfirmCloseTaskQueueElement) {
    await this.updateQueueElementWithErrors(queueElement, queueElement.errors);
  }

  private async storeErrorsInQueueElement(exception: unknown, queueElement: ConfirmCloseTaskQueueElement) {
    // an expired session is handled by the session flow, it says nothing about this operation
    if (exception instanceof UnauthorizedException) {
      return;
    }
    await this.updateQueueElementWithErrors(queueElement, QueueElement.errorsFromException(exception));
  }

  private async updateQueueElementWithErrors(
    queueElement: ConfirmCloseTaskQueueElement,
    errors: Array<QueueElementError>
  ) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isConfirmClosedToServer: this.isConfirmClosedToServer,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isConfirmClosedToServer: this.isConfirmClosedToServer,
    };
    await this.queueService.enqueue(ConfirmCloseTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async saveTaskToExternalServer(task: Task) {
    if (this.isSavedToServer) {
      return;
    }
    await this.taskRepository.save(task);
    this.isSavedToServer = true;
  }

  private async confirmCloseTaskToExternalServer(task: Task) {
    if (this.isConfirmClosedToServer) {
      return;
    }
    await this.taskRepository.confirmClose(task);
    this.isConfirmClosedToServer = true;
  }

  private markTaskAsFinalDependingOnResolution(): void {
    const taskResolutionId = this.task.getConfirmCloseTaskData().resolution;
    if (!taskResolutionId) {
      return;
    }

    if (!this.masterDataService.getConfirmCloseTaskActions().isResolutionFinal(taskResolutionId)) {
      return;
    }

    this.task.markAsFinalState();
  }
}
